import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { inviteClubUserSchema, type ClubDto, type ClubUserDto } from "@tc/shared";
import { Avatar, Button, Card, Field, Icon, Modal, Tag, TextInput, Toast } from "@/components/ui";
import { Can, useAuthz } from "@/lib/auth/authz";
import { useFormatters } from "@/lib/i18n/format";
import { adminApi, ApiError } from "./api";

/** Initials for a user's avatar — first letters of the first two words, uppercased. */
function initialsOf(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] ?? "")
    .join("")
    .toUpperCase();
}

/**
 * The "Club users" card on the club detail page — the people who can sign in to a club's portal.
 * Lists users, invites a new one (full name + email; the server assigns the default club role and
 * emails a set-password link), re-sends a pending invite, and enables/disables a user (disabling
 * revokes their sessions immediately, server-side). Gated to `user.manage`.
 */
export function ClubUsersSection({ club }: { club: ClubDto }) {
  const { t } = useTranslation();
  const { has } = useAuthz();
  const clubId = club.id;

  const [users, setUsers] = useState<ClubUserDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setUsers(await adminApi.listClubUsers(clubId));
    } catch {
      /* surfaced via empty state */
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  useEffect(() => {
    void load();
  }, [load]);

  function flash(message: string) {
    setToast(message);
    window.setTimeout(() => setToast(null), 3500);
  }

  async function setStatus(user: ClubUserDto, status: ClubUserDto["status"]) {
    const updated = await adminApi.updateUser(user.id, { status });
    setUsers((prev) => prev.map((u) => (u.id === user.id ? updated : u)));
  }

  async function resend(user: ClubUserDto) {
    await adminApi.resendInvite(clubId, user.id);
    flash(t("users.resent", { email: user.email }));
  }

  const canManage = has("user.manage");

  return (
    <Card>
      <div className="set-section-head row-between">
        <div>
          <h2>{t("users.sectionTitle")}</h2>
          <div className="sub">{t("users.sectionSub")}</div>
        </div>
        <Can permission="user.manage">
          <Button variant="secondary" icon="plus" onClick={() => setInviteOpen(true)}>
            {t("users.invite")}
          </Button>
        </Can>
      </div>

      {loading ? (
        <div className="empty">{t("common.comingSoon")}</div>
      ) : users.length === 0 ? (
        <div className="empty">
          <div className="empty-ic">
            <Icon name="roster" size={24} />
          </div>
          {t("users.empty")}
        </div>
      ) : (
        <table className="table" style={{ marginTop: 8 }}>
          <thead>
            <tr>
              <th>{t("users.colUser")}</th>
              <th>{t("users.status")}</th>
              <th>{t("users.lastActive")}</th>
              <th aria-label="actions" />
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                canManage={canManage}
                onSetStatus={setStatus}
                onResend={resend}
              />
            ))}
          </tbody>
        </table>
      )}

      {inviteOpen && (
        <InviteModal
          clubId={clubId}
          defaultLanguage={club.defaultLanguage}
          onClose={() => setInviteOpen(false)}
          onInvited={async (email) => {
            setInviteOpen(false);
            await load();
            flash(t("users.inviteSent", { email }));
          }}
        />
      )}

      {toast && <Toast message={toast} />}
    </Card>
  );
}

interface UserRowProps {
  user: ClubUserDto;
  canManage: boolean;
  onSetStatus: (user: ClubUserDto, status: ClubUserDto["status"]) => Promise<void>;
  onResend: (user: ClubUserDto) => Promise<void>;
}

function UserRow({ user, canManage, onSetStatus, onResend }: UserRowProps) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const [busy, setBusy] = useState(false);

  async function guard(fn: () => Promise<void>) {
    setBusy(true);
    try {
      await fn();
    } catch {
      /* keep prior state; a follow-up action can retry */
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr>
      <td>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Avatar initials={initialsOf(user.name)} />
          <div className="t-trip">
            <b>{user.name}</b>
            <small>{user.email}</small>
          </div>
        </div>
      </td>
      <td>
        <UserStatusPill user={user} />
      </td>
      <td>{user.lastLoginAt ? fmt.date(user.lastLoginAt) : t("users.never")}</td>
      <td>
        {canManage && (
          <div className="admin-row-actions">
            {user.pending ? (
              <Button variant="soft" size="sm" disabled={busy} onClick={() => void guard(() => onResend(user))}>
                {t("users.resend")}
              </Button>
            ) : user.status === "active" ? (
              <Button
                variant="soft"
                size="sm"
                disabled={busy}
                onClick={() => void guard(() => onSetStatus(user, "disabled"))}
              >
                {t("users.disable")}
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => void guard(() => onSetStatus(user, "active"))}
              >
                {t("users.enable")}
              </Button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

function UserStatusPill({ user }: { user: ClubUserDto }) {
  const { t } = useTranslation();
  if (user.pending) {
    return (
      <Tag variant="warning" dot>
        {t("users.statusInvited")}
      </Tag>
    );
  }
  const ok = user.status === "active";
  return (
    <Tag variant={ok ? "success" : "danger"} dot>
      {t(ok ? "users.statusActive" : "users.statusDisabled")}
    </Tag>
  );
}

interface InviteModalProps {
  clubId: string;
  defaultLanguage: ClubDto["defaultLanguage"];
  onClose: () => void;
  onInvited: (email: string) => void | Promise<void>;
}

function InviteModal({ clubId, onClose, onInvited }: InviteModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const parsed = inviteClubUserSchema.safeParse({ name, email });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t("users.saveError"));
      return;
    }
    setSaving(true);
    try {
      await adminApi.inviteUser(clubId, parsed.data);
      await onInvited(email);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("users.saveError"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={t("users.inviteTitle")}>
      <form onSubmit={onSubmit}>
        <div className="modal-body">
          {error && (
            <div className="auth-alert" style={{ marginBottom: 16 }} role="alert">
              <Icon name="alert" size={16} className="aa-ic" />
              <div className="aa-body">{error}</div>
            </div>
          )}
          <div className="admin-form">
            <Field label={t("users.name")} required>
              <TextInput value={name} onChange={setName} placeholder={t("users.namePlaceholder")} autoFocus />
            </Field>
            <Field label={t("users.email")} required>
              <TextInput
                icon="mail"
                type="email"
                value={email}
                onChange={setEmail}
                placeholder={t("users.emailPlaceholder")}
              />
            </Field>
          </div>
        </div>
        <div className="modal-foot">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t("actions.cancel")}
          </Button>
          <Button type="submit" icon="mail" disabled={saving}>
            {t("users.sendInvite")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
